import { useRef } from "react";

import { offset } from "@floating-ui/dom";
import { faFile, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { FileCard } from "src/base-components/FileCard";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  MENU_DIVIDER,
  type OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Pill } from "src/base-components/Pill";
import {
  FileAttributes,
  GetFileEnvFilesFileIdGetEnvEnum,
} from "src/clients/files";
import { Accordion } from "src/design-system/Accordion";
import { type Environment } from "src/hooks/useEnvironment";

export type FileCategoryData = {
  id: string;
  display_name: string;
  description?: string;
  category_key: string;
  file_ids: string[];
};

type FileCategoryProps = {
  category: FileCategoryData;
  env: GetFileEnvFilesFileIdGetEnvEnum;
  fileCategories: FileCategoryData[];
  onDeleteFile: (fileId: string, fileName: string, categoryKey: string) => void;
  onMoveFile: (
    fileId: string,
    fileName: string,
    fromCategory: string,
    toCategory: string,
  ) => void;
  canEdit: boolean;
  canDelete: boolean;
  newlyUploadedFileIds?: string[];
};

const buildFileDropdownElements = (
  fileId: string,
  fileName: string,
  categoryKey: string,
  fileCategories: FileCategoryData[],
  canEdit: boolean,
  canDelete: boolean,
  onDeleteFile: FileCategoryProps["onDeleteFile"],
  onMoveFile: FileCategoryProps["onMoveFile"],
): OptionsDropdownElement[] => {
  const elements: OptionsDropdownElement[] = [];

  const otherCategories = fileCategories.filter(
    (cat) => cat.category_key !== categoryKey,
  );

  if (canEdit && otherCategories.length > 0) {
    elements.push(
      { key: "header", header: "Move to:" } as OptionsDropdownElement,
      ...otherCategories.map((cat) => ({
        key: cat.display_name,
        action: () =>
          onMoveFile(fileId, fileName, categoryKey, cat.category_key),
      })),
    );
  }

  if (canDelete) {
    if (elements.length > 0) {
      elements.push(MENU_DIVIDER);
    }
    elements.push({
      key: "Delete file",
      action: () => onDeleteFile(fileId, fileName, categoryKey),
      icon: faTrash,
    });
  }

  return elements;
};

const CaseFileCard: React.FC<{
  fileId: string;
  children: (onView: (file: FileAttributes | File) => void) => React.ReactNode;
}> = ({ fileId, children }) => {
  const { open } = useFloatingWindowsActions();
  const cardRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={cardRef}>
      {children((file) =>
        open(
          {
            id: `file-viewer-${fileId}`,
            type: "cm-file",
            target: cardRef,
            positionOptions: {
              placement: "left-start",
              middleware: [offset({ mainAxis: 60, crossAxis: 0 })],
            },
          },
          { file },
        ),
      )}
    </div>
  );
};

export const FileCategory: React.FC<FileCategoryProps> = ({
  category,
  env,
  fileCategories,
  onDeleteFile,
  onMoveFile,
  canEdit,
  canDelete,
  newlyUploadedFileIds,
}) => {
  const fileCount = category.file_ids.length;
  const hasFiles = fileCount > 0;

  return (
    <Accordion.Item
      readOnly={!hasFiles}
      rightSlot={
        <Pill size="sm" variant="indigo-light">
          <Pill.Icon icon={faFile} />
          <Pill.Text>{fileCount}</Pill.Text>
        </Pill>
      }
      title={category.display_name}
      value={category.category_key}
    >
      <div className="flex flex-col gap-3">
        {category.file_ids.map((fileId) => {
          const isNewlyUploaded = newlyUploadedFileIds?.includes(fileId);

          return (
            <CaseFileCard key={fileId} fileId={fileId}>
              {(onView) => (
                <FileCard
                  badge={
                    isNewlyUploaded ? (
                      <Pill
                        dataLoc={`file-new-pill-${fileId}`}
                        size="sm"
                        variant="green"
                      >
                        <Pill.Text>New</Pill.Text>
                      </Pill>
                    ) : undefined
                  }
                  dataLoc={`file-card-${fileId}`}
                  rightSlot={(fileInfo) => {
                    const elements = buildFileDropdownElements(
                      fileId,
                      fileInfo.name,
                      category.category_key,
                      fileCategories,
                      canEdit,
                      canDelete,
                      onDeleteFile,
                      onMoveFile,
                    );

                    if (elements.length === 0) return null;

                    return (
                      <EllipsisOptionsDropdown
                        dataLoc={`file-options-${fileId}`}
                        elements={elements}
                        iconSize="xs"
                      />
                    );
                  }}
                  fileEnv={env as Environment}
                  fileId={fileId}
                  onView={onView}
                  showCreatedAt
                />
              )}
            </CaseFileCard>
          );
        })}
      </div>
    </Accordion.Item>
  );
};
